import { Switch } from '@thanx/uikit/switch'
import { useStyletron } from '@thanx/uikit/theme'
import { withFormsy } from 'formsy-react'
import { PassDownProps } from 'formsy-react/dist/withFormsy'
import { useMemo, useState } from 'react'
import { InputGroup } from 'react-bootstrap'

import EnhancedMultiSelect from 'components/Form/EnhancedMultiSelect'
import { PillInput } from 'components/Form/PillInput'
import useProducts from 'hooks/useProducts'
import { buildTranslate } from 'locales'
import { Fields as Product } from 'models/Product'
import ProductItems from './ProductItems'

import type { Option } from 'baseui/select'

type ProductSelectProps = PassDownProps<string[]> & {
  label: string
  helpText?: string
  fallbackLabel: string
  fallbackHelpText?: string
  className?: string
  isFreeItem?: boolean
  zeroPricedProductIds?: Set<string>
}

const t = buildTranslate('redeem_manager.product_select')

const ProductSelect = (props: ProductSelectProps) => {
  const {
    name,
    value: selectedIds = [],
    isFreeItem = false,
    setValue,
    helpText,
    label,
    fallbackLabel,
    className = '',
    zeroPricedProductIds,
  } = props
  const [searchTerm, setSearchTerm] = useState('')
  const { products, isLoading, shouldDisplayIds } = useProducts()
  const [isFocused, setIsFocused] = useState(false)
  const [selectionOrder, setSelectionOrder] = useState<string[]>([])
  const [css] = useStyletron()

  const selectedProducts = useMemo(() => {
    const productIds = selectionOrder.length ? selectionOrder : selectedIds

    return productIds
      .map(id => products.find(product => product.chain_uid === id))
      .filter(Boolean) as Product[]
  }, [selectionOrder, selectedIds, products])

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleChange = (selections: Option[]) => {
    const newSelectedIds = selections.map(p => p.chain_uid)
    setValue(newSelectedIds)
    setSelectionOrder(newSelectedIds)
  }

  const deleteItem = (uid: string) => {
    const newSelectedIds = selectedIds.filter(id => id !== uid)
    setValue(newSelectedIds)
    setSelectionOrder(previous => previous.filter(id => id !== uid))
  }

  if (shouldDisplayIds) {
    return (
      <div>
        <Switch condition={isFreeItem}>
          <label id={name} className="body-text-4 bold">
            {t('free_item_title')}
          </label>
          <p className="body-text-4">{fallbackLabel}</p>
        </Switch>

        <Switch condition={!isFreeItem}>
          <label id={name} className="body-text-4 bold">
            {fallbackLabel}
          </label>
        </Switch>

        <p className="mt-xs body-text-4 grey-70">{t('help_text')}</p>

        <PillInput
          name={name}
          selectedIds={selectedIds}
          setValue={setValue}
          className={className}
          placeholder={t('placeholder')}
        />
      </div>
    )
  }

  return (
    <div className={className}>
      <p className="body-text-14 bold">{label}</p>
      <Switch condition={Boolean(helpText)}>
        <p className="mt-xs body-text-4 grey-70">{helpText}</p>
      </Switch>
      <div className="d-flex flex-row">
        <Switch condition={isFocused}>
          <InputGroup.Addon
            className={css({
              width: '46px',
              height: '48px',
            })}
          >
            <i className="fa fa-search pt-xxs"></i>
          </InputGroup.Addon>
        </Switch>
        <EnhancedMultiSelect
          placeholder={isFocused ? t('search') : t('select_item')}
          maxDropdownHeight="400px"
          isLoading={isLoading}
          options={filteredProducts}
          onSearch={setSearchTerm}
          valueName="item"
          labelKey="name"
          valueKey="chain_uid"
          value={selectedProducts}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onChange={selections => handleChange(selections)}
        />
      </div>
      <div className="mt-s">
        <ProductItems
          products={selectedProducts}
          deleteItem={deleteItem}
          zeroPricedProductIds={zeroPricedProductIds}
        />
      </div>
    </div>
  )
}

export default withFormsy(ProductSelect)
